import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { apiLoginUrl, apiRequest } from '../services/apiClient';

const AuthContext = createContext(null);

// Fallback bundle if the role map hasn't loaded — the base `user` role.
const USER_CAPABILITIES = ['read_contracts'];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Preview-as-role: null = off, otherwise the role being simulated.
  // Migrates the old boolean preview-as-user key on first read.
  const [previewRole, setPreviewRoleState] = useState(() => {
    try {
      const stored = localStorage.getItem('docket:previewRole');
      if (stored) return stored;
      return localStorage.getItem('docket:previewAsUser') === '1' ? 'user' : null;
    } catch { return null; }
  });
  const setPreviewRole = (role) => {
    try {
      if (role) localStorage.setItem('docket:previewRole', role);
      else localStorage.removeItem('docket:previewRole');
      localStorage.removeItem('docket:previewAsUser');
    } catch { /* ignore */ }
    setPreviewRoleState(role || null);
  };

  // "View as user" (doc-323) — a SERVER-side lens, unlike previewRole above.
  // The backend swaps the whole session's identity, so /auth/me already comes
  // back as the target and there is no client state to hold: starting/exiting
  // is one call plus a full reload so every mounted page refetches through the
  // new lens (and a page the target can't reach isn't left on screen).
  const startViewAs = async (userId) => {
    await apiRequest('/admin/view-as', { method: 'POST', json: { userId } });
    window.location.assign('/');
  };
  const exitViewAs = async () => {
    await apiRequest('/admin/view-as', { method: 'DELETE' });
    window.location.assign('/');
  };

  useEffect(() => {
    apiRequest('/auth/me')
      .then(userData => {
        setUser(userData);
        setLoading(false);
      })
      .catch(() => {
        setUser(null);
        setLoading(false);
      });
  }, []);

  const login = () => {
    window.location.href = apiLoginUrl();
  };

  const logout = async () => {
    try {
      await apiRequest('/auth/logout');
      setUser(null);
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  // Capability-derived permissions. The backend computes user.capabilities from
  // the role (single source of truth) and sends it on /auth/me; the frontend
  // mirrors those gates for UX only — the backend always re-enforces.
  //
  // While previewing a role, capabilities collapse to that role's bundle
  // (served by the backend on /auth/me for admins) so the whole app renders
  // as that role would see it. Pure visual simulation — the real role is
  // unchanged and the backend still authorizes by it. Per-user grants are
  // deliberately NOT applied in preview: it shows the role, not a person.
  const value = useMemo(() => {
    const role = user?.role || null;
    // While viewing-as, `user` IS the target and `role` is THEIR role — so
    // realIsAdmin can't be derived from it, or the exit control would vanish
    // the moment you view as a non-admin. The block's presence is the proof
    // that an admin is behind this session (the backend only ever sends it to
    // one).
    const viewAs = user?.viewAs?.active ? user.viewAs : null;
    const realIsAdmin = Boolean(viewAs) || role === 'admin';
    const realCapabilities = Array.isArray(user?.capabilities) ? user.capabilities : [];
    const roleCapabilities = user?.roleCapabilities || {};
    const previewableRoles = Object.keys(roleCapabilities).filter((r) => r !== 'admin');
    // The two lenses never stack: a server-side view-as session wins, and the
    // role-preview selector stays out of reach until it's exited.
    const previewing = !viewAs && Boolean(previewRole) && realIsAdmin && previewRole !== 'admin';
    const capabilities = previewing
      ? (roleCapabilities[previewRole] || USER_CAPABILITIES)
      : realCapabilities;
    const can = (cap) => capabilities.includes(cap);
    return {
      user,
      loading,
      login,
      logout,
      role: previewing ? previewRole : role,
      capabilities,
      can,
      // Derived from capabilities so role/cap changes flow through automatically.
      isAdmin: can('manage_roles'),
      canWriteContracts: can('write_contracts'),
      canUploadContracts: can('upload_contracts'),
      canReviewRequests: can('review_legal') || can('review_security') || can('review_compliance'),
      // Preview controls — realIsAdmin keeps the selector reachable while previewing.
      realIsAdmin,
      previewRole: previewing ? previewRole : null,
      setPreviewRole,
      previewableRoles,
      // Back-compat alias for any straggler consumers of the old boolean.
      previewAsUser: previewing,
      // View-as (doc-323): read-only, server-enforced, one specific person.
      viewAs,
      startViewAs,
      exitViewAs,
    };
  }, [user, loading, previewRole]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
